import { ActionResult } from '~/hooks/actions/enums';
import { parseMultiSelectValue } from '~/hooks/preference-selector/helpers';
import type { Feed } from '~/services/layout/index.ts';
import {
  prepareComponentsData,
  type LoadRemoteFeedFn,
} from './data-source-factory.ts';
import {
  DEFAULT_FORM_VALIDATION_MESSAGES,
  type FormValidationMessages,
} from './form-validation-messages.ts';
import {
  FormComponentTypes,
  type ErrorsValidationResolver,
  type FormConfig,
  type FormField,
  type FormUiComponent,
  type FormValueValidator,
  type LayoutPresetsInput,
  type UIComponentsDataSources,
} from './types.ts';

const ERROR_BORDER_STYLES: Record<string, string | number> = {
  component_padding_bottom: 5,
  component_padding_top: 5,
  component_padding_right: 5,
  component_padding_left: 5,
  borderColor: '#FF0000',
  borderWidth: 2,
};

/** Tracks validation errors and decorates components for inline error display. */
class ErrorManager {
  validationErrors: Map<string, string[]> | null = null;
  previousValidationErrors: Map<string, string[]> | null = null;

  compareValidationErrors(
    previous: Map<string, string[]> | null,
    current: Map<string, string[]>
  ): ErrorsValidationResolver {
    if (!previous) return { fullyResolved: [], partiallyResolved: [] };

    const fullyResolved: string[] = [];
    const partiallyResolved: string[] = [];

    previous.forEach((prevMsgs, key) => {
      const currentMsgs = current.get(key);

      if (!current.has(key) || !currentMsgs || currentMsgs.length === 0) {
        fullyResolved.push(key);
        return;
      }

      if (prevMsgs.some((msg) => !currentMsgs.includes(msg))) {
        partiallyResolved.push(key);
      }
    });

    return { fullyResolved, partiallyResolved };
  }

  validateComponentsWithData(
    data: Record<string, string>,
    validators: Map<string, { fieldId: string; validator: FormValueValidator }>
  ): ErrorsValidationResolver {
    const componentErrors = new Map<string, string[]>();

    validators.forEach(({ validator, fieldId }, componentId) => {
      componentErrors.set(componentId, validator(data[fieldId]));
    });

    const resolved = this.compareValidationErrors(
      this.previousValidationErrors,
      componentErrors
    );

    this.previousValidationErrors = new Map(componentErrors);
    this.validationErrors = componentErrors;

    return resolved;
  }

  hasValidationErrors(): boolean {
    return (
      this.validationErrors !== null &&
      Array.from(this.validationErrors.values()).some(
        (errors) => errors.length > 0
      )
    );
  }

  mapComponentsWithValidatedData(
    uiComponents: FormUiComponent[]
  ): FormUiComponent[] {
    if (!this.validationErrors) return uiComponents;

    return uiComponents.map((component) => {
      const componentErrors = this.validationErrors?.get(component.id);
      const hasErrors = Boolean(componentErrors?.length);

      if (component.component_type === 'group-qb' && component.ui_components) {
        return {
          ...component,
          ui_components: component.ui_components.map((child) => {
            if (child.component_type === 'group-info-qb') return child;
            return hasErrors
              ? { ...child, addition_styles: ERROR_BORDER_STYLES }
              : { ...child, addition_styles: undefined };
          }),
        };
      }

      return hasErrors
        ? {
            ...component,
            addition_styles: ERROR_BORDER_STYLES,
            data: {
              ...component.data,
              validation_error: componentErrors!.join(', '),
            },
          }
        : {
            ...component,
            addition_styles: undefined,
            data: component.data
              ? { ...component.data, validation_error: undefined }
              : component.data,
          };
    });
  }
}

type FormDataModelListener<T> = (value: T) => void;

/**
 * Orchestrates form configuration, component building, and validation.
 */
export class FormDataModel {
  private qbFormData: FormConfig | null = null;
  private uiComponents: FormUiComponent[] = [];
  private uiComponentsDataSource: UIComponentsDataSources = {};
  private readonly errorManager = new ErrorManager();
  private isErrorAllowedToPresent = false;
  private readonly componentListeners = new Set<
    FormDataModelListener<FormUiComponent[]>
  >();

  private readonly layoutPresets: LayoutPresetsInput;
  private readonly screenId: string;
  private readonly loadRemoteFeed?: LoadRemoteFeedFn;
  private readonly validationMessages: FormValidationMessages;

  /**
   * @param layoutPresets - Layout preset mapping from Zapp configuration.
   * @param screenId - Current screen id.
   * @param loadRemoteFeed - Optional loader for select field remote item feeds.
   * @param validationMessages - Localized validation copy. Defaults to English.
   */
  constructor(
    layoutPresets: LayoutPresetsInput,
    screenId: string,
    loadRemoteFeed?: LoadRemoteFeedFn,
    validationMessages: FormValidationMessages = DEFAULT_FORM_VALIDATION_MESSAGES
  ) {
    this.layoutPresets = layoutPresets;
    this.screenId = screenId;
    this.loadRemoteFeed = loadRemoteFeed;
    this.validationMessages = validationMessages;
  }

  getValidationMessages(): FormValidationMessages {
    return this.validationMessages;
  }

  /**
   * Builds the form from a remote form feed configuration (Option A).
   *
   * @param formConfig - Remote form configuration with fields and buttons.
   * @param data - Current screen state values keyed by field id.
   */
  prepareFormDataFromConfig(
    formConfig: FormConfig,
    data: Record<string, string>
  ): void {
    if (!Array.isArray(formConfig?.properties)) {
      throw new Error(
        "prepareFormDataFromConfig: invalid form feed config, 'properties' array is required"
      );
    }

    this.qbFormData = { properties: [...formConfig.properties] };
    this.buildComponents(data);
  }

  subscribeComponents(
    listener: FormDataModelListener<FormUiComponent[]>
  ): () => void {
    this.componentListeners.add(listener);
    return () => this.componentListeners.delete(listener);
  }

  getComponents(): FormUiComponent[] {
    return this.uiComponents;
  }

  validateFormScreenActionFired(): ActionResult {
    if (!this.errorManager.hasValidationErrors()) {
      return ActionResult.Success;
    }

    this.isErrorAllowedToPresent = true;
    this.proceedValidatedComponents();
    return ActionResult.Cancel;
  }

  getDataSource(id: string): () => Promise<Feed> {
    const source = this.uiComponentsDataSource[id];
    if (!source) {
      throw new Error(
        `getDataSource: data source for component ${id} not found`
      );
    }
    return source.dataSource;
  }

  private proceedValidatedComponents(): void {
    this.uiComponents = this.errorManager.mapComponentsWithValidatedData(
      this.uiComponents
    );
    this.emitComponents(this.uiComponents);
  }

  validateFormData(data: Record<string, string>): ErrorsValidationResolver {
    return this.errorManager.validateComponentsWithData(
      data,
      this.getValidators()
    );
  }

  requestComponentValidation(data: Record<string, string>): void {
    const resolvedErrors = this.validateFormData(data);

    if (!this.isErrorAllowedToPresent) return;

    if (
      resolvedErrors.fullyResolved.length > 0 ||
      resolvedErrors.partiallyResolved.length > 0
    ) {
      this.proceedValidatedComponents();
    }
  }

  normalizeValues(data: Record<string, unknown>): Record<string, unknown> {
    if (!this.qbFormData) return { ...data };

    const normalizedData: Record<string, unknown> = { ...data };

    this.qbFormData.properties.forEach((property: FormField) => {
      if (property.type === FormComponentTypes.CheckBox) {
        const value = data[property.id];
        if (value == null) {
          normalizedData[property.id] = false;
        } else if (typeof value !== 'boolean') {
          normalizedData[property.id] = value === 'true' || value === '1';
        }
        return;
      }

      if (property.type !== FormComponentTypes.MultiSelect) return;

      const value = data[property.id];
      if (Array.isArray(value)) {
        normalizedData[property.id] = value;
        return;
      }

      if (typeof value !== 'string') {
        normalizedData[property.id] = [];
        return;
      }

      normalizedData[property.id] = parseMultiSelectValue(value);
    });

    return normalizedData;
  }

  private buildComponents(data: Record<string, string>): void {
    if (!this.qbFormData) return;

    const { uiComponents, uiComponentsDataSource } = prepareComponentsData({
      formDataSource: this.qbFormData,
      layoutPresets: this.layoutPresets,
      screenId: this.screenId,
      loadRemoteFeed: this.loadRemoteFeed,
      validationMessages: this.validationMessages,
    });

    this.uiComponents = uiComponents;
    this.uiComponentsDataSource = uiComponentsDataSource;
    this.validateFormData(data);
    this.emitComponents(uiComponents);
  }

  private emitComponents(components: FormUiComponent[]): void {
    this.componentListeners.forEach((listener) => listener(components));
  }

  private getValidators(): Map<
    string,
    { fieldId: string; validator: FormValueValidator }
  > {
    return new Map(
      Object.entries(this.uiComponentsDataSource).map(
        ([componentId, entry]) => [
          componentId,
          { fieldId: entry.fieldId, validator: entry.validator },
        ]
      )
    );
  }
}
